import type { IScope, IGroup, IRule } from '@/types/filterBuilderTypes';
import { isGroup, isRule } from '@/types/typeguards/QueryBuilder';
import { isEndBeforeStart } from '@/utils/date';
import { LIST_SEARCH_RULE_ID } from '@/utils/listSearch';

/**
 * Recursively filters out rules with null or empty values from a filter scope.
 * Rules with null values should never be sent to the backend.
 * 
 * @param scope - The filter scope to clean
 * @returns A cleaned scope with invalid rules removed, or null if the scope becomes empty
 */
export function filterNullValues(scope: IScope | null | undefined): IScope | null {
  if (!scope) {
    return null;
  }

  if (isRule(scope)) {
    // Also filter out rules without a column or criterion (incomplete rules)
    if (!scope.column || !scope.criterion) {
      return null;
    }

    // For is_empty_or_null and is_not_empty_or_null, value may be null or omitted per API
    const isNullCriterion =
      scope.criterion === 'is_empty_or_null' || scope.criterion === 'is_not_empty_or_null';
    if (isNullCriterion) {
      return { ...scope, value: null };
    }

    // "between" carries both ends of a date range, and the backend requires both. A range
    // with one end still to pick is a half-built rule, so it is not sent.
    if (scope.criterion === 'between') {
      return isCompleteRange(scope.value) ? scope : null;
    }

    // For all other criteria, filter out rules with null or empty value
    if (scope.value === null || scope.value === undefined) {
      return null;
    }

    return scope;
  }

  if (isGroup(scope)) {
    // For groups, recursively filter rules
    const filteredRules = scope.rules
      .map(rule => filterNullValues(rule))
      .filter((rule): rule is IScope => rule !== null);

    // If all rules were filtered out, return null
    if (filteredRules.length === 0) {
      return null;
    }

    // If only one rule remains and it's not a group, we could unwrap it
    // But for now, we'll keep the group structure
    return {
      ...scope,
      rules: filteredRules,
    };
  }

  return null;
}

/** Whether a "between" rule's value holds both ends, in either order: `['2026-11-01', '2026-11-07']`. */
export function hasBothRangeEnds(value: unknown): value is [string, string] {
  return (
    Array.isArray(value)
    && value.length === 2
    && value.every((end) => typeof end === 'string' && end !== '')
  );
}

/**
 * Whether a "between" rule's value holds both ends, in order. A range whose end is before
 * its start matches nothing, so it counts as unfinished: it is not sent and cannot be starred.
 */
export function isCompleteRange(value: unknown): value is [string, string] {
  return hasBothRangeEnds(value) && !isEndBeforeStart(value[0], value[1]);
}

/**
 * Structural deep-equality for filter scopes. Ignores the random `id`
 * field on rules / groups (those are UI bookkeeping and regenerate on
 * each builder session) so the comparison reflects "have the actual rule
 * contents diverged?" rather than identity churn. Used by the unified
 * Filter Builder to decide whether the active saved preset is "modified"
 * relative to the in-flight builder state.
 */
export function scopesEqual(a: IScope | null | undefined, b: IScope | null | undefined): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  if (isRule(a) && isRule(b)) {
    return rulesEqual(a, b);
  }
  if (isGroup(a) && isGroup(b)) {
    return groupsEqual(a, b);
  }
  return false;
}

function rulesEqual(a: IRule, b: IRule): boolean {
  // Two list-search rules are the same search when their text is the same. A view saved
  // before SAU-467 searched one column; it now opens as a quick search of the same text,
  // and must not look modified just because the rule's shape changed.
  if (a.id === LIST_SEARCH_RULE_ID && b.id === LIST_SEARCH_RULE_ID) {
    return valuesEqual(a.value, b.value);
  }
  return (
    a.column === b.column
    && a.criterion === b.criterion
    && (a.field ?? null) === (b.field ?? null)
    && valuesEqual(a.value, b.value)
  );
}

function groupsEqual(a: IGroup, b: IGroup): boolean {
  if (a.scopeType !== b.scopeType) {
    return false;
  }
  if (a.rules.length !== b.rules.length) {
    return false;
  }
  for (let i = 0; i < a.rules.length; i++) {
    if (!scopesEqual(a.rules[i], b.rules[i])) {
      return false;
    }
  }
  return true;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  // Treat null / undefined / empty string as equivalent — the builder
  // normalises these inconsistently across cells, and a "modified" flip
  // every time a value field is focused/blurred would be noise.
  const isBlank = (v: unknown) => v === null || v === undefined || v === '';
  if (isBlank(a) && isBlank(b)) {
    return true;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((item, idx) => valuesEqual(item, b[idx]));
  }
  return false;
}
